import logoUrl from "../../assets/csc_logo_transparent.png";
import styles from "./Login.module.css";
import mcpAx from "../../config/mcpAxios";
import { useEffect, useState, ReactNode } from "react";

export type OAuthRequestInfo = {
  client_name: string;
  redirect_host: string;
  scopes: string[];
};

export type OAuthRequestState =
  | { status: "loading" }
  | { status: "ready"; info: OAuthRequestInfo }
  | { status: "expired" };

const EXPIRED_MESSAGE =
  "This sign-in link has expired. Start the connection again from your app.";

// Loads the pending MCP authorization request named by ?oauth_request=<id>.
// Does nothing when there is no request id, so normal login is unaffected.
export function useOAuthRequest(
  oauthRequest: string | null,
): OAuthRequestState | null {
  const [state, setState] = useState<OAuthRequestState | null>(
    oauthRequest ? { status: "loading" } : null,
  );

  useEffect(() => {
    if (!oauthRequest) {
      setState(null);
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    mcpAx
      .get(`oauth/request/${encodeURIComponent(oauthRequest)}`)
      .then((result) => {
        if (!cancelled) setState({ status: "ready", info: result.data });
      })
      .catch((error) => {
        console.debug("oauth request lookup failed:");
        console.log(error);
        if (!cancelled) setState({ status: "expired" });
      });
    return () => {
      cancelled = true;
    };
  }, [oauthRequest]);

  return state;
}

function ConsentCard(props: { children: ReactNode }) {
  return (
    <div className={"card mx-auto auth-card w-100 " + styles.loginContainer}>
      <div className="card-body">
        <div className="w-100 text-center mb-2">
          <img
            src={logoUrl}
            alt="AG Logo"
            className={"mt-2 mb-4 mx-auto " + styles.companyLogo}
          />
        </div>
        {props.children}
      </div>
    </div>
  );
}

export function OAuthExpired() {
  return (
    <ConsentCard>
      <div className="text-danger text-center mt-2">
        <p>{EXPIRED_MESSAGE}</p>
      </div>
    </ConsentCard>
  );
}

export function OAuthConsent(props: {
  oauthRequest: string;
  info: OAuthRequestInfo;
  email: string;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");

  const decide = async (approve: boolean) => {
    setIsSubmitting(true);
    setMessage("");
    try {
      const result = await mcpAx.post("oauth/complete", {
        oauth_request: props.oauthRequest,
        approve: approve,
      });
      // Full-page navigation: the target is the MCP client, not a route in this app.
      window.location.href = result.data.redirect;
    } catch (error: any) {
      console.debug("oauth complete error:");
      console.log(error);
      switch (error.response?.status) {
        case 400:
          setMessage(EXPIRED_MESSAGE);
          break;
        case 401:
          setMessage("Your session has expired. Please refresh and log in again.");
          break;
        default:
          setMessage(
            "Unable to complete the connection. Please try again later or contact support.",
          );
      }
      setIsSubmitting(false);
    }
  };

  return (
    <ConsentCard>
      <div className="text-center">
        <h5 className="card-title mb-4 px-3">Connect an app</h5>
        <p>
          <strong>{props.info.client_name}</strong> wants to access your
          account as <strong>{props.email}</strong>.
        </p>
        <p>
          <small className="text-muted">
            You will be returned to {props.info.redirect_host}.
          </small>
        </p>
      </div>
      <div className="text-center">
        <button
          className="btn btn-primary w-75 mt-3"
          disabled={isSubmitting}
          onClick={() => decide(true)}
        >
          Allow
        </button>
        <button
          className="btn btn-outline-secondary w-75 mt-3"
          disabled={isSubmitting}
          onClick={() => decide(false)}
        >
          Deny
        </button>
      </div>
      {message !== "" && (
        <div className="text-danger text-center mt-4">
          <p>{message}</p>
        </div>
      )}
    </ConsentCard>
  );
}

export default OAuthConsent;
